import { arsenalScore } from './score';
import type { Fixture, PredictionPick } from './types';

export type MatchdayPassport = {
  /** Matches with a confirmed contribution from this browser, not page views. */
  followed: number;
  calls: number;
  settledCalls: number;
  correctCalls: number;
  accuracy: number | null;
  gauges: number;
  streak: number;
};

function readJson(key: string): Record<string, unknown> | null {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as Record<string, unknown> : null;
  } catch { return null; }
}

function participated(): Set<string> {
  try {
    const value = JSON.parse(localStorage.getItem('coyg_participated') ?? '[]') as unknown;
    return new Set(Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);
  } catch { return new Set(); }
}

function predicted(matchId: string): PredictionPick | null {
  const mine = readJson(`coyg_predict_cache:${matchId}`)?.mine;
  return mine === 'W' || mine === 'D' || mine === 'L' ? mine : null;
}

function gauged(matchId: string): boolean {
  const mine = readJson(`coyg_gauge_cache:${matchId}`)?.mine;
  return Boolean(mine && typeof mine === 'object' && Object.values(mine as Record<string, unknown>).some((value) => value != null && value !== ''));
}

function result(fixture: Fixture): PredictionPick | null {
  if (fixture.status !== 'ft') return null;
  if (fixture.venue === 'N' && fixture.arsenalSide !== 'home' && fixture.arsenalSide !== 'away') return null;
  if (!fixture.score || ![fixture.score.home, fixture.score.away].every((value) => Number.isInteger(value) && value >= 0)) return null;
  const score = arsenalScore(fixture.score, fixture.venue, fixture.arsenalSide);
  if (!score) return null;
  return score.ours > score.theirs ? 'W' : score.ours < score.theirs ? 'L' : 'D';
}

export function buildMatchdayPassport(fixtures: Fixture[]): MatchdayPassport {
  const own = participated();
  const completed = fixtures.filter((fixture) => result(fixture) !== null).sort((a, b) => Date.parse(b.kickoffUtc ?? '') - Date.parse(a.kickoffUtc ?? ''));
  let calls = 0; let settledCalls = 0; let correctCalls = 0; let gauges = 0;
  fixtures.forEach((fixture) => {
    const call = predicted(fixture.id); const outcome = result(fixture);
    if (call) {
      calls += 1;
      if (outcome) { settledCalls += 1; if (call === outcome) correctCalls += 1; }
    }
    if (gauged(fixture.id)) gauges += 1;
  });
  let streak = 0;
  for (const fixture of completed) {
    if (!own.has(fixture.id)) break;
    streak += 1;
  }
  return { followed: fixtures.filter((fixture) => own.has(fixture.id)).length, calls, settledCalls, correctCalls, accuracy: settledCalls ? Math.round((correctCalls / settledCalls) * 100) : null, gauges, streak };
}
